import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Loader2, Star, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import StarRating from "@/components/StarRating";

/**
 * LeaveReviewDialog — auto-prompted right after a buyer confirms delivery
 * (and also available manually from any delivered order). Posts to
 * POST /api/reviews — which is idempotent per (buyer, listing).
 */
const LABELS = {
  1: "Terrible — would not recommend",
  2: "Poor — fell short",
  3: "OK — average experience",
  4: "Good — would recommend",
  5: "Amazing — exceeded expectations",
};

const LeaveReviewDialog = ({ open, onClose, listing, sellerName, onSubmitted, autoTriggered = false }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setRating(0); setComment(""); setSubmitting(false); };
  const handleClose = () => { reset(); onClose(); };

  const submit = async () => {
    if (!rating || comment.trim().length < 3) {
      toast.error("Please choose a rating and add a short comment");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        listing_id: listing?.listing_id,
        rating,
        comment: comment.trim(),
      });
      toast.success("Thanks for your review — it really helps the seller and other buyers");
      onSubmitted?.();
      handleClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit review");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent data-testid="leave-review-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Star className="w-5 h-5 text-[#D9923B] fill-[#D9923B]" /> Rate your experience
          </DialogTitle>
          <DialogDescription>
            {autoTriggered
              ? "Glad it arrived! Quick rating + 1-line review — your honesty helps the next buyer."
              : "Share your honest experience with this seller and listing."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {listing && (
            <div className="flex items-center gap-3 p-3 rounded-md bg-[#F3EFE9]">
              {listing.images?.[0] && (
                <img src={listing.images[0]} alt={listing.title} className="w-14 h-14 rounded-md object-cover shrink-0" />
              )}
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{listing.title}</div>
                {sellerName && (
                  <div className="text-xs text-muted-foreground">sold by <span className="font-semibold">{sellerName}</span></div>
                )}
              </div>
            </div>
          )}
          <div className="text-center py-2">
            <StarRating
              value={rating}
              onChange={setRating}
              size={36}
              testIdPrefix="review-star"
            />
            <div className="text-xs text-muted-foreground mt-2 min-h-[16px]">
              {rating > 0 ? LABELS[rating] : "Tap a star to rate"}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Your review</label>
            <textarea
              data-testid="review-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="What stood out? How was the quality, communication, packaging…"
              className="w-full mt-1 px-3 py-2 rounded-md border border-black/15 bg-white text-sm focus:border-[#C05A3C] focus:outline-none focus:ring-2 focus:ring-[#C05A3C]/30"
            />
            <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
              <BadgeCheck className="w-3 h-3 text-[#2A3D36]" /> Your review will be marked as a <span className="font-semibold">Verified buyer</span>.
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            {autoTriggered ? "Maybe later" : "Cancel"}
          </Button>
          <Button
            data-testid="review-submit-btn"
            onClick={submit}
            disabled={submitting || !rating || comment.trim().length < 3}
            className="bg-[#C05A3C] hover:bg-[#A8492F] text-white"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Star className="w-4 h-4 mr-2" />}
            Submit review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LeaveReviewDialog;
